/**
 * Import Service
 * API client pour les opérations d'import
 */

import { fetchWithAuth, safeJsonParse } from '@/utils/api';
import type {
  Import,
  ImportType,
  ImportConfig,
  ImportPreview,
  ValidationResult,
  ImportResult,
  ImportsListResponse,
  ImportTypesResponse,
  ImportStatus,
  UploadResponse,
  ImportLinesParams,
  ImportLinesResponse,
  UpdateImportLineRequest,
  UpdateImportLineResponse,
  RevalidateResponse,
} from '../types/import.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const importService = {
  /**
   * Récupère les types d'import disponibles
   * Backend retourne: { success, data: [{ value, label, description, supportedFormats }] }
   */
  async getTypes(): Promise<ImportTypesResponse> {
    const response = await fetchWithAuth(`${API_URL}/imports/types`, {
      method: 'GET',

    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la récupération des types');
    }

    // L'API retourne { success: true, data: [...types avec supportedFormats] }
    const result = await safeJsonParse<ApiResponse<ImportTypesResponse['types']>>(response);
    return { types: result.data || [] };
  },

  /**
   * Upload un fichier pour import
   * Backend retourne: { success, data: { id, type, format, status, originalFileName, originalFileSize, storagePath, url } }
   */
  async upload(
    file: File,
    type: ImportType,
    config?: ImportConfig
  ): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (config) {
      formData.append('config', JSON.stringify(config));
    }

    // fetchWithAuth gère automatiquement FormData (pas de Content-Type) et le token
    const response = await fetchWithAuth(`${API_URL}/imports/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
      let errorMessage = "Erreur lors de l'upload du fichier";
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `Erreur ${response.status}: ${response.statusText || errorMessage}`;
      }
      throw new Error(errorMessage);
    }

    const result = await safeJsonParse<ApiResponse<UploadResponse>>(response);
    return result.data;
  },

  /**
   * Récupère le preview d'un import
   * Timeout élevé pour le parsing de gros fichiers
   */
  async getPreview(importId: string): Promise<ImportPreview> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/preview`, {
      method: 'GET',

      timeout: 120000, // 2 minutes pour le parsing
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la récupération du preview');
    }

    const result = await safeJsonParse<ApiResponse<ImportPreview>>(response);
    return result.data;
  },

  /**
   * Met à jour la configuration d'un import
   */
  async updateConfig(importId: string, config: ImportConfig): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/config`, {
      method: 'PUT',

      body: JSON.stringify(config),
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la mise à jour de la configuration');
    }
  },

  /**
   * Valide un import
   * Timeout élevé pour les gros imports (25k+ lignes)
   */
  async validate(importId: string): Promise<ValidationResult> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/validate`, {
      method: 'POST',

      timeout: 300000, // 5 minutes pour les gros imports
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || "Erreur lors de la validation de l'import");
    }

    const result = await safeJsonParse<ApiResponse<ValidationResult>>(response);
    return result.data;
  },

  /**
   * Lance le traitement d'un import
   * Timeout élevé pour les gros imports
   */
  async process(importId: string): Promise<ImportResult> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/process`, {
      method: 'POST',

      timeout: 600000, // 10 minutes pour le traitement complet
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || "Erreur lors du traitement de l'import");
    }

    const result = await safeJsonParse<ApiResponse<ImportResult>>(response);
    return result.data;
  },

  /**
   * Récupère le statut d'un import
   */
  async getStatus(importId: string): Promise<Import> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/status`, {
      method: 'GET',

    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la récupération du statut');
    }

    const result = await safeJsonParse<ApiResponse<Import>>(response);
    return result.data;
  },

  /**
   * Récupère l'historique des imports
   * Backend retourne: { success, data: Import[], meta: { total, totalPages, currentPage, limit } }
   */
  async getHistory(params?: {
    type?: ImportType;
    status?: ImportStatus;
    page?: number;
    limit?: number;
  }): Promise<ImportsListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = queryParams.toString()
      ? `${API_URL}/imports?${queryParams.toString()}`
      : `${API_URL}/imports`;

    const response = await fetchWithAuth(url, {
      method: 'GET',

    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || "Erreur lors de la récupération de l'historique");
    }

    // Backend retourne { data: Import[], meta: { total, totalPages, currentPage, limit } }
    const result = await safeJsonParse<{
      success: boolean;
      data: Import[];
      meta: { total: number; totalPages: number; currentPage: number; limit: number };
    }>(response);

    // Transformer en format ImportsListResponse
    return {
      imports: result.data || [],
      total: result.meta?.total ?? 0,
      totalPages: result.meta?.totalPages ?? 1,
      currentPage: result.meta?.currentPage ?? 1,
      limit: result.meta?.limit ?? 20,
    };
  },

  /**
   * Annule un import en cours
   */
  async cancel(importId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}`, {
      method: 'DELETE',

    });

    if (!response.ok) {
      const errorData = await safeJsonParse<{
        error?: { message?: string; code?: string } | string;
        message?: string
      }>(response);
      // Backend peut retourner { error: { message: "..." } } ou { message: "..." } ou { error: "..." }
      const errorMessage = typeof errorData.error === 'object'
        ? errorData.error?.message
        : errorData.error || errorData.message;
      throw new Error(errorMessage || "Erreur lors de l'annulation de l'import");
    }
  },

  /**
   * Rollback un import traité
   * Note: Le rollback est asynchrone (BullMQ job), donc on reçoit un jobId
   */
  async rollback(importId: string): Promise<{ jobId: string; importId: string; message: string }> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/rollback`, {
      method: 'POST',

    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || "Erreur lors du rollback de l'import");
    }

    const result = await safeJsonParse<ApiResponse<{ jobId: string; importId: string; message: string }>>(response);
    return result.data;
  },

  /**
   * Télécharge le fichier original d'un import
   */
  async downloadFile(importId: string): Promise<Blob> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/download`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Erreur lors du téléchargement du fichier');
    }

    return response.blob();
  },

  // ============================================================================
  // MÉTHODES POUR LA GESTION PAGINÉE DES LIGNES D'IMPORT (Enterprise UX)
  // ============================================================================

  /**
   * Récupère les lignes d'un import avec pagination, filtres et tri
   */
  async getImportLines(importId: string, params?: ImportLinesParams): Promise<ImportLinesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) {
      if (Array.isArray(params.status)) {
        queryParams.append('status', params.status.join(','));
      } else {
        queryParams.append('status', params.status);
      }
    }
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = queryParams.toString()
      ? `${API_URL}/imports/${importId}/lines?${queryParams.toString()}`
      : `${API_URL}/imports/${importId}/lines`;

    const response = await fetchWithAuth(url, {
      method: 'GET',

    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la récupération des lignes');
    }

    const result = await safeJsonParse<{
      success: boolean;
      data: ImportLinesResponse['lines'];
      meta: { total: number; totalPages: number; currentPage: number; limit: number };
      stats: ImportLinesResponse['stats'];
    }>(response);

    return {
      lines: result.data || [],
      total: result.meta?.total ?? 0,
      totalPages: result.meta?.totalPages ?? 1,
      currentPage: result.meta?.currentPage ?? 1,
      limit: result.meta?.limit ?? 50,
      stats: result.stats || { pending: 0, validated: 0, error: 0, warning: 0, skipped: 0, processed: 0 },
    };
  },

  /**
   * Met à jour une ligne d'import (correction inline)
   */
  async updateImportLine(
    importId: string,
    lineId: string,
    data: UpdateImportLineRequest
  ): Promise<UpdateImportLineResponse> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/lines/${lineId}`, {
      method: 'PATCH',

      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la mise à jour de la ligne');
    }

    const result = await safeJsonParse<ApiResponse<UpdateImportLineResponse>>(response);
    return result.data;
  },

  /**
   * Exporte les lignes en erreur au format CSV
   * Retourne l'URL de téléchargement ou déclenche le téléchargement
   */
  async exportImportErrors(importId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/lines/export-errors`, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de l\'export des erreurs');
    }

    // Récupérer le nom du fichier depuis le header Content-Disposition
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'erreurs_import.csv';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) filename = match[1];
    }

    // Déclencher le téléchargement
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Revalide toutes les lignes d'un import
   * Timeout élevé car peut traiter des milliers de lignes
   */
  async revalidateImport(importId: string): Promise<RevalidateResponse> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/revalidate`, {
      method: 'POST',

      timeout: 300000, // 5 minutes pour les gros imports
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la revalidation');
    }

    const result = await safeJsonParse<ApiResponse<RevalidateResponse>>(response);
    return result.data;
  },

  /**
   * Réinitialise un import bloqué (PROCESSING ou FAILED)
   * Permet de relancer le traitement après un timeout ou crash
   */
  async resetStuckImport(
    importId: string,
    targetStatus?: 'VALIDATED' | 'PARSED'
  ): Promise<{ previousStatus: string; newStatus: string }> {
    const response = await fetchWithAuth(`${API_URL}/imports/${importId}/reset`, {
      method: 'POST',

      body: targetStatus ? JSON.stringify({ targetStatus }) : undefined,
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la réinitialisation');
    }

    const result = await safeJsonParse<ApiResponse<{ previousStatus: string; newStatus: string }>>(response);
    return result.data;
  },

  /**
   * Extraire les données d'une facture via OCR
   * Appelle POST /api/v1/ocr/extract
   */
  async extractInvoiceOCR(file: File): Promise<{
    success: boolean;
    data?: {
      provider: string;
      extractedData: {
        invoiceNumber?: string | null;
        invoiceDate?: string | null;
        dueDate?: string | null;
        supplier?: {
          name?: string | null;
          siret?: string | null;
          tvaNumber?: string | null;
          address?: string | null;
        };
        amounts?: {
          subtotal?: number | null;
          tax?: number | null;
          total?: number | null;
        };
        lineItems?: Array<{
          quantity?: number;
          description?: string;
          unitPrice?: number;
          totalHT?: number;
          vatRate?: number;
        }>;
        currency?: string;
        confidence: number;
      };
      processingTime?: number;
    };
    error?: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithAuth(`${API_URL}/ocr/extract`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de l\'extraction OCR');
    }

    return safeJsonParse(response);
  },

  /**
   * Créer une facture à partir de données OCR confirmées
   * Appelle POST /api/v1/ocr/create-invoice
   */
  async createInvoiceFromOCR(data: {
    invoiceNumber?: string;
    invoiceDate: string;
    dueDate: string;
    client: {
      id?: string;
      companyName?: string;
      fullName?: string;
      email?: string;
      vatNumber?: string;
      siret?: string;
      address?: string;
    };
    items: Array<{
      description: string;
      quantity: number;
      unitPrice: number;
      vatRate: number;
    }>;
    title?: string;
    internalNotes?: string;
    currencyCode?: string;
  }): Promise<{
    success: boolean;
    data: {
      invoice: {
        id: string;
        number: string;
        status: string;
        totalHT: number;
        totalTVA: number;
        totalTTC: number;
      };
      clientId: string;
      isNewClient: boolean;
    };
  }> {
    const response = await fetchWithAuth(`${API_URL}/ocr/create-invoice`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse<{ error?: string; message?: string }>(response);
      throw new Error(error.message || error.error || 'Erreur lors de la création de la facture');
    }

    return safeJsonParse(response);
  },
};
